import { useGetLoggedInUser } from '@services/api/users';
import { alpha, Link, Stack, SxProps, Typography } from '@mui/material';
import { ProfilePictureAvatar } from '@components/Avatars/ProfiePictureAvatar';
import { useTranslations } from '@services/hooks/translations/useTranslations';
import { Dropdown } from '@components/Dropdown/Dropdown';
import { useToggle } from '@hooks/useToggle';
import { mdiChevronDown } from '@mdi/js';
import { useSignOut } from '@services/api/authenticate/signOut';
import { useCallback, useMemo } from 'react';
import MenuItem from '@mui/material/MenuItem';
import { useLocation } from 'react-router';
import { buttonMediumSize } from '../../styles/theme/theme';
import { Theme } from '@mui/material/styles';

export const UserProfile = () => {
  const {
    translate,
    translations: {
      myAccount: { header },
      common: { buttons },
    },
  } = useTranslations();
  const { user } = useGetLoggedInUser();
  const [isDropdownOpen, toggleIsDropdownOpen] = useToggle(false);
  const { signOut } = useSignOut();
  const currentRoute = useLocation().pathname;

  const isActive = currentRoute.includes(`/my-account`);

  const onLogOut = useCallback(() => {
    signOut();
  }, [signOut]);

  const userHasProfilePicture = Boolean(user?.logotypeUri);
  const sxForAvatar: SxProps<Theme> = useMemo(
    () =>
      userHasProfilePicture
        ? (theme) => ({
            '& .MuiAvatar-root::after': {
              content: `""`,
              position: `absolute`,
              top: 0,
              left: 0,
              width: `100%`,
              height: `100%`,
              backgroundColor: isActive ? alpha(theme.palette.rexelEnergy.blackBlue, 0.2) : `transparent`,
              borderRadius: `50%`,
            },
            '&:hover': {
              '& .MuiAvatar-root::after': {
                backgroundColor: alpha(theme.palette.primary.light, 0.2),
              },
            },
          })
        : (theme) => ({
            '&:hover': {
              '& .MuiAvatar-root': {
                backgroundColor: theme.palette.primary.light,
                color: theme.palette.primary.dark,
              },
            },
            '& .MuiAvatar-root': {
              backgroundColor: isActive ? theme.palette.rexelEnergy.blackBlue : theme.palette.common.white,
              svg: {
                color: isActive ? theme.palette.common.white : theme.palette.rexelEnergy.ctaBlue,
              },
            },
          }),
    [userHasProfilePicture, isActive],
  );

  return (
    <Stack justifyContent="space-between" direction="row">
      <Link href={`/my-account`} underline="none" sx={{ flexGrow: 1 }} data-testid="user-profile">
        <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={2} padding={1} sx={sxForAvatar}>
          <ProfilePictureAvatar addPictureIcon whiteBackground />
          <Stack direction="column" justifyContent="center" alignItems="flex-start">
            <Typography variant="h3" color="inherit">{`${user?.firstName} ${user?.lastName}`}</Typography>
            <Typography variant="subtitle1" color="inherit">
              {translate(header)}
            </Typography>
          </Stack>
        </Stack>
      </Link>
      <Dropdown
        isDropdownOpen={isDropdownOpen}
        toggleIsDropdownOpen={toggleIsDropdownOpen}
        iconButtonIconPath={mdiChevronDown}
        styles={{
          padding: 1.5,
          minWidth: buttonMediumSize,
          height: buttonMediumSize,
          minHeight: buttonMediumSize,
          '& svg': {
            color: `inherit`,
          },
        }}
        buttonId="user-profile-dropdown"
      >
        <MenuItem onClick={onLogOut}>{translate(buttons.logOut)}</MenuItem>
      </Dropdown>
    </Stack>
  );
};
